/**
 * Nederlandse feestdagen (CAO Schoonmaak art. 22.2) per kalenderjaar.
 *
 * Eén lijst voor elk pad dat een dag op feestdag toetst: verlofuren
 * (leave-hours.ts geeft 0 op een feestdag, voor vakantie én ziekte), de
 * verlofbeoordeling (FEESTDAG_IN_RANGE), de team-app preview. Daarvóór had
 * de team-app een eigen lijst zonder 1e Paas-/Pinksterdag en boekte een
 * vakantie over de jaarwissel 7,6u op Nieuwjaarsdag.
 *
 * Bevrijdingsdag telt alleen in lustrumjaren (2025, 2030, ...). Goede Vrijdag
 * is geen vrije dag in de cao.
 *
 * ═══ BRON VAN WAARHEID ═══
 * Byte-identieke spiegels (Vercel-functions importeren niet buiten hun
 * app-root, Vite-apps geen workspace-deps). Wijzig je iets, wijzig dan ALLE
 * DRIE en draai scripts/smoke-workforce.sh (hash-pariteitscheck):
 *   1. brands/cinco/apps/office-app/api/_lib/feestdagen.ts   (canoniek)
 *   2. brands/cinco/apps/office-app/src/lib/feestdagen.ts
 *   3. apps/team-app/api/_lib/feestdagen.ts
 */

export interface Feestdag {
  date: string; // YYYY-MM-DD
  name: string;
}

/** Paaszondag (anonieme Gregoriaanse methode). */
export function paaszondag(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function fmt(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function plusDagen(date: Date, days: number): Date {
  const r = new Date(date);
  r.setDate(r.getDate() + days);
  return r;
}

const cache = new Map<number, Feestdag[]>();

/** Alle feestdagen van een jaar, chronologisch. */
export function feestdagen(year: number): Feestdag[] {
  const hit = cache.get(year);
  if (hit) return hit;
  const pasen = paaszondag(year);
  const lijst: Feestdag[] = [
    { date: `${year}-01-01`, name: 'Nieuwjaarsdag' },
    { date: fmt(pasen), name: '1e Paasdag' },
    { date: fmt(plusDagen(pasen, 1)), name: '2e Paasdag' },
    { date: `${year}-04-27`, name: 'Koningsdag' },
    { date: fmt(plusDagen(pasen, 39)), name: 'Hemelvaartsdag' },
    { date: fmt(plusDagen(pasen, 49)), name: '1e Pinksterdag' },
    { date: fmt(plusDagen(pasen, 50)), name: '2e Pinksterdag' },
    { date: `${year}-12-25`, name: '1e Kerstdag' },
    { date: `${year}-12-26`, name: '2e Kerstdag' },
  ];
  if (year % 5 === 0) lijst.push({ date: `${year}-05-05`, name: 'Bevrijdingsdag' });
  lijst.sort((x, y) => x.date.localeCompare(y.date));
  cache.set(year, lijst);
  return lijst;
}

/** Naam van de feestdag op deze datum, anders null. */
export function feestdagNaam(dateISO: string): string | null {
  const year = parseInt(dateISO.slice(0, 4), 10);
  if (!Number.isFinite(year)) return null;
  return feestdagen(year).find(f => f.date === dateISO)?.name ?? null;
}

export function isFeestdag(dateISO: string): boolean {
  return feestdagNaam(dateISO) !== null;
}

/** Feestdagen binnen een datumbereik (inclusief), chronologisch. */
export function feestdagenTussen(from: string, to: string): Feestdag[] {
  const y1 = parseInt(from.slice(0, 4), 10);
  const y2 = parseInt(to.slice(0, 4), 10);
  const uit: Feestdag[] = [];
  for (let y = y1; y <= y2; y++) uit.push(...feestdagen(y));
  return uit.filter(f => f.date >= from && f.date <= to);
}
